const parseDate = (dateString) => {
  if (!dateString) return null;

  const normalized = String(dateString).replace(/[-.]/g, "/");
  const parts = normalized.split(/[\/T ]/);

  let day, month, year;

  if (parts.length >= 3) {
    if (parts[0].length === 4) {
      year = parts[0];
      month = parts[1];
      day = parts[2];
    } else if (parts[2].length === 4) {
      year = parts[2];

      if (Number(parts[0]) > 12) {
        day = parts[0];
        month = parts[1];
      } else if (Number(parts[1]) > 12) {
        day = parts[1];
        month = parts[0];
      } else {
        day = parts[0];
        month = parts[1];
      }
    }
  }

  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return new Date(dateString);

  return date;
};

const pad = (n) => String(n).padStart(2, "0");

const formatDateForInput = (dateString) => {
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDateForTable = (dateString) => {
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return "N/A";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const formatDateWithTime = (dateString) => {
  const date = new Date(dateString);
  if (!date || isNaN(date.getTime())) return "N/A";

  return (
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

// Cost formatting
function formatCost(value) {
  if (value == null || value === "") return "₹0";
  const num = Number(value);
  if (isNaN(num)) return "₹0";
  if (num % 1 !== 0) {
    return "₹" + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return "₹" + num.toLocaleString("en-IN");
}

// Rate formatting (strictly 2 decimal places for uncompromised Quantity × Rate arithmetic check)
function formatRate(value) {
  if (value == null || value === "") return "₹0.00";
  const num = Number(value);
  if (isNaN(num)) return "₹0.00";
  return "₹" + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Get today for input field
const getToday = () => {
  const today = new Date();
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

// INR currency format
const formatINR = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  });

// Group rows by date
function groupByDate(rows) {
  const grouped = {};
  rows.forEach((row) => {
    const formattedDate =
      formatDateForTable(row.attendance_date) || "Unknown Date";
    if (!grouped[formattedDate]) grouped[formattedDate] = [];
    grouped[formattedDate].push(row);
  });
  return Object.entries(grouped);
}

const formatDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return "";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const getDateRange = (type) => {
  const now = new Date();
  let start, end = new Date();

  switch (type) {
    case "today":
      start = new Date(now.setHours(0, 0, 0, 0));
      break;
    case "yesterday":
      start = new Date(now.setDate(now.getDate() - 1));
      end = new Date(start);
      break;
    case "last7":
      start = new Date(now.setDate(now.getDate() - 7));
      break;
    case "last30":
      start = new Date(now.setDate(now.getDate() - 30));
      break;
    case "thisMonth":
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case "lastYear":
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31);
      break;
    default:
      return {};
  }

  return { start, end };
};

const convertToIndianWords = (input) => {
  if (input == null || input === "") return "Zero Rupees Only /-";
  let num = Math.round(Number(input));
  if (isNaN(num) || num === 0) return "Zero Rupees Only /-";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  const inWords = (n) => {
    n = Math.floor(Number(n) || 0);
    if (n === 0) return "";
    if (n < 20) return a[n] || "";
    if (n < 100) {
      const ten = b[Math.floor(n / 10)] || "";
      const one = a[n % 10] || "";
      return (ten + (one ? " " + one : "")).trim();
    }
    if (n < 1000) {
      const hundred = (a[Math.floor(n / 100)] || "") + " Hundred";
      const rem = inWords(n % 100);
      return (hundred + (rem ? " " + rem : "")).trim();
    }
    if (n < 100000) {
      const thousand = inWords(Math.floor(n / 1000)) + " Thousand";
      const rem = inWords(n % 1000);
      return (thousand + (rem ? " " + rem : "")).trim();
    }
    if (n < 10000000) {
      const lakh = inWords(Math.floor(n / 100000)) + " Lakh";
      const rem = inWords(n % 100000);
      return (lakh + (rem ? " " + rem : "")).trim();
    }
    const crore = inWords(Math.floor(n / 10000000)) + " Crore";
    const rem = inWords(n % 10000000);
    return (crore + (rem ? " " + rem : "")).trim();
  };

  const result = inWords(num);
  return (result ? result.replace(/\s+/g, " ").trim() : "Zero") + " Rupees Only /-";
};

const getLogoUrl = (logo) => {
  if (!logo || typeof logo !== "string") return "/assets/images/sabariconstructions.png";
  if (logo.startsWith("http://") || logo.startsWith("https://") || logo.startsWith("data:")) {
    return logo;
  }
  const clean = logo.replace(/\\/g, "/").replace(/^\/+/, "");
  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  if (!isLocal && process.env.REACT_APP_API_URL) {
    try {
      const origin = new URL(process.env.REACT_APP_API_URL).origin;
      return `${origin}/${clean}`;
    } catch (e) {}
  }
  return `/${clean}`;
};

export {
  formatCost,
  formatRate,
  formatDateWithTime,
  formatDateForTable,
  formatDateForInput,
  getToday,
  formatINR,
  groupByDate,
  formatDate,
  getDateRange,
  convertToIndianWords,
  getLogoUrl,
};
